import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    XCircle, Search, UserPlus, Building2, Heart, User,
    CheckSquare, Square, Loader2
} from 'lucide-react';
import { supabase } from '../../services/supabase';

// Source table metadata — mirrors module colours for recognition
const SOURCE_CONFIG = {
    company: {
        label: 'Company',
        colorClass: 'bg-purple-100 text-purple-700 border-purple-200',
        Icon: Building2,
        tableName: 'clients_coy',
        nameField: 'company_name',
        refField: 'company_number',
    },
    charity: {
        label: 'Charity',
        colorClass: 'bg-sky-100 text-sky-700 border-sky-200',
        Icon: Heart,
        tableName: 'clients_charity',
        nameField: 'charity_name',
        refField: 'charity_number',
    },
    individual: {
        label: 'Individual',
        colorClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        Icon: User,
        tableName: 'clients_individual',
        nameField: 'individual_name',
        refField: 'individual_utr',
    },
    unregistered: {
        label: 'Unregistered',
        colorClass: 'bg-teal-100 text-teal-700 border-teal-200',
        Icon: Heart,
        tableName: 'clients_unregistered',
        nameField: 'unregistered_name',
        refField: 'unregistered_ref',
    },
    partnership: {
        label: 'Partnership',
        colorClass: 'bg-amber-100 text-amber-700 border-amber-200',
        Icon: Building2,
        tableName: 'clients_partnership',
        nameField: 'partnership_name',
        refField: 'partnership_ref',
    },
};

const EnrolClientModal = ({
    onClose,
    onEnrol,
    targetTable = 'clients_books',
    sourceTypes = ['company', 'charity', 'individual'],
    moduleLabel = 'Bookkeeping',
}) => {
    const [availableClients, setAvailableClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [selected, setSelected] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAll = async () => {
            try {
                // Fetch already-enrolled source keys so we can exclude them
                const { data: enrolled } = await supabase
                    .from(targetTable)
                    .select('source_type, source_id');

                const enrolledSet = new Set(
                    (enrolled || []).map(e => `${e.source_type}:${e.source_id}`)
                );

                const enabledTypes = sourceTypes.filter(type => SOURCE_CONFIG[type]);

                // Fetch from enabled source tables in parallel
                const sourceResults = await Promise.all(
                    enabledTypes.map(type => {
                        const src = SOURCE_CONFIG[type];
                        return supabase
                            .from(src.tableName)
                            .select(`id, ${src.nameField}, ${src.refField}`)
                            .order(src.nameField)
                            .then(result => ({ type, result }));
                    })
                );

                const all = sourceResults.flatMap(({ type, result }) => {
                    const src = SOURCE_CONFIG[type];
                    return (result.data || []).map(row => ({
                        key: `${type}:${row.id}`,
                        source_type: type,
                        source_id: row.id,
                        name: row[src.nameField],
                        ref: row[src.refField],
                    }));
                }).filter(c => !enrolledSet.has(c.key));

                setAvailableClients(all);
            } catch (err) {
                setError('Failed to load clients: ' + (err?.message || 'Unknown error'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchAll();
    }, [sourceTypes, targetTable]);

    const counts = useMemo(() => {
        const base = { all: availableClients.length };
        sourceTypes.forEach(type => {
            base[type] = availableClients.filter(c => c.source_type === type).length;
        });
        return base;
    }, [availableClients, sourceTypes]);

    const filteredClients = useMemo(() => {
        const search = searchTerm.toLowerCase();
        return availableClients.filter(c => {
            const matchesType = typeFilter === 'all' || c.source_type === typeFilter;
            const matchesSearch = !searchTerm
                || c.name?.toLowerCase().includes(search)
                || c.ref?.toLowerCase().includes(search);
            return matchesType && matchesSearch;
        });
    }, [availableClients, typeFilter, searchTerm]);

    const allFilteredSelected = filteredClients.length > 0 && filteredClients.every(c => selected.has(c.key));

    const handleToggle = (key) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleToggleAll = () => {
        if (allFilteredSelected) {
            setSelected(prev => {
                const next = new Set(prev);
                filteredClients.forEach(c => next.delete(c.key));
                return next;
            });
        } else {
            setSelected(prev => {
                const next = new Set(prev);
                filteredClients.forEach(c => next.add(c.key));
                return next;
            });
        }
    };

    const handleConfirm = async () => {
        if (selected.size === 0) return;
        setIsSaving(true);
        setError('');
        try {
            const toEnrol = availableClients.filter(c => selected.has(c.key));
            await onEnrol(toEnrol);
        } catch (err) {
            setError(err?.message || 'Enrolment failed. Please try again.');
            setIsSaving(false);
        }
    };

    const TAB_LABELS = {
        company: 'Companies',
        charity: 'Charities',
        individual: 'Individuals',
        unregistered: 'Unregistered',
        partnership: 'Partnerships',
    };

    const TABS = [
        { key: 'all', label: 'All', count: counts.all || 0 },
        ...sourceTypes
            .filter(type => SOURCE_CONFIG[type])
            .map(type => ({
                key: type,
                label: TAB_LABELS[type] || SOURCE_CONFIG[type]?.label || type,
                count: counts[type] || 0,
            })),
    ];

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                {/* ── Header ── */}
                <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-teal-600 to-emerald-600 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <UserPlus className="w-5 h-5" />
                                Enrol Clients in {moduleLabel}
                            </h3>
                            <p className="text-xs text-teal-100 mt-0.5">
                                Select existing clients to add to the {moduleLabel.toLowerCase()} roster
                            </p>
                        </div>
                        <button onClick={onClose} className="text-teal-100 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors">
                            <XCircle className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* ── Search + Type Tabs ── */}
                <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex-shrink-0 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by name or reference..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            autoFocus
                        />
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setTypeFilter(tab.key)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                    typeFilter === tab.key
                                        ? 'bg-teal-600 text-white shadow-sm'
                                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                                }`}
                            >
                                {tab.label}
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    typeFilter === tab.key
                                        ? 'bg-white/25 text-white'
                                        : 'bg-slate-100 text-slate-500'
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Client List ── */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm">Loading clients...</span>
                        </div>
                    ) : filteredClients.length === 0 ? (
                        <div className="py-16 text-center text-slate-400">
                            <div className="text-sm font-medium">
                                {availableClients.length === 0
                                    ? `All eligible clients are already enrolled in ${moduleLabel}.`
                                    : 'No clients match your search.'}
                            </div>
                            {availableClients.length === 0 && (
                                <div className="text-xs mt-1 text-slate-300">
                                    Add new clients on the Companies, Charities or Individuals pages first.
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Select-all header row */}
                            <div
                                className="px-6 py-2.5 flex items-center gap-3 bg-slate-50 border-b border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors"
                                onClick={handleToggleAll}
                            >
                                <div className={allFilteredSelected ? 'text-teal-600' : 'text-slate-400'}>
                                    {allFilteredSelected
                                        ? <CheckSquare className="w-4 h-4" />
                                        : <Square className="w-4 h-4" />
                                    }
                                </div>
                                <span className="text-xs font-semibold text-slate-500">
                                    {allFilteredSelected
                                        ? `Deselect all ${filteredClients.length} shown`
                                        : `Select all ${filteredClients.length} shown`}
                                </span>
                            </div>

                            {/* Client rows */}
                            {filteredClients.map(client => {
                                const src = SOURCE_CONFIG[client.source_type];
                                const isSelected = selected.has(client.key);
                                const { Icon } = src;
                                return (
                                    <div
                                        key={client.key}
                                        onClick={() => handleToggle(client.key)}
                                        className={`px-6 py-3 flex items-center gap-3 cursor-pointer border-b border-slate-50 transition-colors ${
                                            isSelected ? 'bg-teal-50 hover:bg-teal-100' : 'hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className={`flex-shrink-0 ${isSelected ? 'text-teal-600' : 'text-slate-300'}`}>
                                            {isSelected
                                                ? <CheckSquare className="w-4 h-4" />
                                                : <Square className="w-4 h-4" />
                                            }
                                        </div>
                                        <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-teal-500' : 'text-slate-400'}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-semibold truncate ${isSelected ? 'text-teal-800' : 'text-slate-700'}`}>
                                                {client.name || '(no name)'}
                                            </div>
                                            {client.ref && (
                                                <div className="text-xs text-slate-400 font-mono">{client.ref}</div>
                                            )}
                                        </div>
                                        <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${src.colorClass}`}>
                                            {src.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex-shrink-0">
                    {error && (
                        <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-4">
                        <div className="text-sm text-slate-500">
                            {selected.size > 0 ? (
                                <span className="font-semibold text-teal-700">
                                    {selected.size} client{selected.size !== 1 ? 's' : ''} selected
                                </span>
                            ) : (
                                <span className="text-slate-400">Select clients to enrol</span>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={selected.size === 0 || isSaving}
                                className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all"
                            >
                                <UserPlus className="w-4 h-4" />
                                {isSaving
                                    ? 'Enrolling...'
                                    : `Enrol${selected.size > 0 ? ` ${selected.size}` : ''} Client${selected.size !== 1 ? 's' : ''}`
                                }
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default EnrolClientModal;
